import { useRef, useState } from "react"
import {
  Autocomplete,
  Avatar,
  Box,
  Popper,
  PopperProps,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import SearchIcon from "@mui/icons-material/Search"
import { countryList, findCountry, TCountryEntry, TCountryOption } from "../../core/phone"
import { useTranslation } from "react-i18next"
import { getCountryName } from "../../core/i18n/countryNames"

/** Remove acentos para que "mexico" case com "México". */
const semAcento = (s: string) =>
  s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()

export const filtrarPaises = (
  paises: TCountryOption[],
  termo: string,
  nomeLocalizado: (c: TCountryOption) => string = (c) => c.namePtBr
): TCountryOption[] => {
  const t = semAcento(termo.trim()).replace(/^\+/, "")
  if (!t) return paises
  return paises.filter(
    (c) =>
      semAcento(c.namePtBr).includes(t) ||
      semAcento(nomeLocalizado(c)).includes(t) ||
      c.ddi.startsWith(t) ||
      c.iso.toLowerCase() === t
  )
}

/**
 * A lista NÃO herda a largura do campo (128px) — ancorada à esquerda com
 * largura própria, senão nome e DDI saem cortados/quebrados em 3 linhas.
 */
const PopperLista = (props: PopperProps) => (
  <Popper
    {...props}
    placement="bottom-start"
    style={{ ...props.style, width: 300, maxWidth: "calc(100vw - 32px)" }}
  />
)

type Props = {
  /** ISO do país selecionado. */
  value: string
  onChange: (iso: string) => void
  disabled?: boolean
}

/**
 * Largura do cluster fechado (bandeira + DDI + seta), fixa — não por
 * conteúdo. DDI varia de 1 a 3 dígitos ("+1" a "+995"); largura por
 * conteúdo fazia o cluster crescer com DDIs de 3 dígitos e sobrepor o
 * "ml" fixo que os layouts aplicam no label do campo ao lado. Calibrada
 * para caber confortavelmente o pior caso (3 dígitos) sem folga grande
 * nos casos menores.
 */
export const COUNTRY_SELECT_CLOSED_WIDTH = 92

/**
 * Deslocamento do label do campo vizinho (ex: "Celular") quando ele está
 * vazio e o label ainda não encolheu — precisa ficar depois de todo o
 * cluster (seletor fechado + divisor). Único ponto de cálculo: os layouts
 * que usam CountrySelect devem importar esta constante em vez de
 * hardcodar o número, senão as duas larguras podem voltar a divergir.
 * Em spacing units do MUI (theme.spacing(1) = 8px por padrão):
 * COUNTRY_SELECT_CLOSED_WIDTH (92px) + divisor com margens (18px) = 110px.
 */
export const COUNTRY_SELECT_LABEL_OFFSET = 110 / 8

const opcoes = countryList()

export const CountrySelect = ({ value, onChange, disabled }: Props) => {
  const { t, i18n } = useTranslation()
  const idioma = i18n.resolvedLanguage ?? "pt-BR"
  // pt-BR usa o nome do registry (como sempre); os outros idiomas, o nome do Intl.
  const nomePais = (c: TCountryEntry) =>
    idioma === "pt-BR" ? c.namePtBr : getCountryName(c.iso, idioma, c.namePtBr)
  const atual = findCountry(value)

  // Input controlado: fechado mostra só bandeira + DDI (adornment) — o nome do
  // país no input espremia "+55Brasil" e aparecia selecionado em azul no foco.
  // O texto digitado existe apenas enquanto a lista está aberta, para busca.
  const [busca, setBusca] = useState("")
  const [aberta, setAberta] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Fechar via CSS (caretColor/width) não é suficiente: o input nativo
  // continua com foco do DOM depois de abrir e escolher/Esc, e o navegador
  // insiste em desenhar o cursor de texto ali — sobrando um tracinho ao lado
  // da seta mesmo com a caixa fechada. Tirar o foco de verdade resolve.
  const fecharEDesfocar = () => {
    setAberta(false)
    inputRef.current?.blur()
  }

  return (
    <>
    <Autocomplete
      options={opcoes}
      value={opcoes.find((c) => c.iso === value) ?? opcoes[0]}
      onChange={(_, novo) => novo && onChange(novo.iso)}
      disabled={disabled}
      disableClearable
      blurOnSelect
      // Sem o icone padrao (forcePopupIcon abaixo) e com o input colapsado
      // quando fechado, o clique nao tinha mais quem abrisse a lista: o
      // toggle era do icone. Abrir no foco devolve isso — clicar em
      // qualquer ponto do cluster (bandeira/DDI/seta) foca o input e abre.
      openOnFocus
      open={aberta}
      inputValue={busca}
      onInputChange={(_, valor, motivo) =>
        setBusca(motivo === "input" ? valor : "")
      }
      onOpen={() => setAberta(true)}
      onClose={fecharEDesfocar}
      // A seta padrao do Autocomplete ancora na borda DIREITA do campo (128px),
      // longe do "+55" — parecia pertencer ao input de celular ao lado. A seta
      // e renderizada dentro do adorno, colada ao DDI (ver renderInput).
      forcePopupIcon={false}
      filterOptions={(lista, estado) => filtrarPaises(lista, estado.inputValue, nomePais)}
      getOptionLabel={(c) => nomePais(c)}
      isOptionEqualToValue={(a, b) => a.iso === b.iso}
      PopperComponent={PopperLista}
      ListboxProps={{ style: { maxHeight: 320 } }}
      // Fechado, largura FIXA (COUNTRY_SELECT_CLOSED_WIDTH) — não por
      // conteúdo. Largura por conteúdo ("auto") fazia DDI de 3 dígitos
      // (ex: +355) crescer o cluster e sobrepor o label do campo vizinho,
      // que usa um "ml" fixo calibrado para essa mesma constante — ver
      // COUNTRY_SELECT_LABEL_OFFSET. Aberto, 128px para a caixa de busca.
      sx={{
        width: aberta ? 128 : COUNTRY_SELECT_CLOSED_WIDTH,
        flexShrink: 0,
        // Trava a largura de verdade — sem isso, um DDI que não coubesse
        // nos 92px calculados vazaria visualmente em vez de respeitar o
        // limite (o overflow não afeta a legibilidade: o cluster nunca
        // chega perto de precisar rolar, é só garantia contra estouro).
        overflow: "hidden",
      }}
      renderOption={(props, c) => (
        <Box component="li" {...props} key={c.iso} sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0, width: "100%" }}
          >
            <Avatar
              alt={nomePais(c)}
              src={c.flag}
              sx={{ width: 22, height: 22, flexShrink: 0 }}
            />
            <Typography fontSize={14} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {nomePais(c)}
            </Typography>
            <Typography fontSize={13} color="text.secondary" sx={{ flexShrink: 0 }}>
              +{c.ddi}
            </Typography>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          variant="standard"
          placeholder={aberta ? t("country.search") : ""}
          sx={
            aberta
              ? // Aberto: cursor normal piscando — é uma caixa de busca de
                // verdade (com a lupa), então o caret é esperado ao digitar.
                undefined
              : {
                  // Fechado, o input de busca colapsa (senão o min-width
                  // interno do Autocomplete empurra o cursor para fora do
                  // campo) e o caret some — sem isso ele sobra como um traço
                  // solto acima do placeholder do campo vizinho.
                  "& .MuiAutocomplete-input": {
                    width: 0,
                    minWidth: 0,
                    flexGrow: 0,
                    padding: 0,
                    overflow: "hidden",
                    caretColor: "transparent",
                  },
                }
          }
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            // Aberto, o adorno sai e os 128px inteiros viram a caixa de busca.
            startAdornment:
              !aberta && atual ? (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  onClick={() => !disabled && setAberta(true)}
                  sx={{ mr: 0.5, flexShrink: 0, cursor: "pointer" }}
                >
                  <Avatar
                    alt={nomePais(atual)}
                    src={`/assets/icons/countries/1x1/${atual.iso}.svg`}
                    sx={{ width: 22, height: 22 }}
                  />
                  <Typography fontSize={14}>+{atual.ddi}</Typography>
                  <ArrowDropDownIcon
                    fontSize="small"
                    sx={{ color: "action.active", ml: -0.25 }}
                  />
                </Stack>
              ) : aberta ? (
                // Aberto: lupa como indicador claro de que dá pra pesquisar
                // (o placeholder "Buscar" sozinho passava despercebido).
                <SearchIcon
                  fontSize="small"
                  sx={{ color: "action.active", flexShrink: 0, mr: 0.5 }}
                />
              ) : null,
          }}
          inputProps={{ ...params.inputProps, "data-cy": "customerPhoneCountry" }}
        />
      )}
    />
    {!aberta && (
      // Divisor visual entre o seletor e o campo de celular ao lado. Fica
      // aqui (não nos layouts que usam este componente) porque só o
      // CountrySelect sabe se está aberto — nos layouts, era um <Box>
      // estático sempre renderizado, sobrando sozinho acima do placeholder
      // "Celular" quando a lista abria (o Popper é portal, não empurra
      // este elemento, que continuava no lugar de antes).
      <Box
        sx={{
          backgroundColor: "#DCDDDE",
          height: "22.5px",
          width: "2px",
          ml: 1,
          mr: 1,
        }}
      />
    )}
    </>
  )
}
